import React, { useCallback, useMemo } from 'react';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { Column } from 'components/display/Table/types';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { QuoteTableRowKey } from '../types/enums';
import GenericBadge from 'components/ui/Badge';
import BadgeTooltip from 'components/ui/BadgeTooltip';
import { QuoteRow } from '../types/interfaces';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { IdLabel } from 'api/risk';
import { useNavigate } from 'react-router-dom';
import { Status } from 'types/optim/status';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';
import SelectBrokerBranches from 'features/dashboard/dashboard-menu/SelectBrokerBranches';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { useSharedViewDetection } from 'shared/contexts/table-context/useSharedViewDetection';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { QuoteFetchParams } from 'slices/generic-table/hooks/useQuoteTable';
import { mapQuoteSummaries } from '../mappers/mapQuoteSummaryToQuote';
import { GetGenericListCommandDto, QuoteSummaryDto } from 'api/quote';
import { quotesAPI } from 'services/swagger-api';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { FileText, Trash2 } from 'lucide-react';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import { useDocumentViewerDialog } from 'hooks/useDocumentViewerDialog';
import { DocumentScopeEnum } from 'api/documents';
import { formatDay } from 'core/utils/dates';

interface QuoteViewProps {
  hideCreate?: boolean;
  /** Liste admin BO (toutes plateformes) : routes /admin/liste + colonnes Courtier/Plateforme. */
  admin?: boolean;
}

/** Dates CALENDAIRES (date d'effet, validité) : découpe de string, zéro fuseau. */
const fmtDate = (iso?: string): string => (iso ? formatDay(iso) : '—');

// Colonnes masquées par défaut côté courtier (évite le scroll ; réactivables via « Colonnes »)
const COURTIER_HIDDEN_COLUMNS = ['tunnelLabel', 'support', 'effectiveDate', 'endDate'];

const QuoteView: React.FC<QuoteViewProps> = ({ hideCreate = false, admin = false }) => {
  // Vue partagée : colonne Courtier si brokerName rempli par le back (détecteur données).
  const { isSharedView, detectFromItems } = useSharedViewDetection();
  // Droits pilotés par le rôle actif (manager central) : devis = Courtier uniquement
  // (admin & Plateforme en lecture seule) pour le clic ET la colonne d'actions.
  const canOpenProcess = useRoleCan(Capability.QuoteOpen);
  const canRowActions = useRoleCan(Capability.QuoteActions);
  const canSeeNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const { t } = useDynamicTranslation([
    NsKey.Common,
    NsKey.Quote,
    NsKey.Risk,
    NsKey.Pricing,
    NsKey.Contract
  ]);
  const { createMenuItem } = useActionMenu();

  const navigate = useNavigate();

  const { openBlankDialog } = useDialog();
  const { openDocumentViewer } = useDocumentViewerDialog();

  const onNewQuote = useCallback(() => {
    openBlankDialog(
      {
        size: Sizes['5xl']
      },
      <SelectBrokerBranches />
    );
  }, [openBlankDialog]);

  const goToContractualizationStep = useCallback(
    (riskId: string, quoteId: string, borrowerProjectId?: string) => {
      if (borrowerProjectId) {
        navigate(`/simulation/contractualisation/borrower/${borrowerProjectId}/${riskId}/${quoteId}`);
      } else {
        navigate(`/simulation/contractualisation/${riskId}/${quoteId}`);
      }
    },
    [navigate]
  );

  const columns: Column<QuoteRow>[] = useMemo(
    () => [
      {
        key: QuoteTableRowKey.DATE,
        label: t('COMMON.DATE'),
        sortable: true,
        sortKey: 'date',
        render: (value) => {
          const date = new Date(value as string);
          const formattedDate = format(date, 'dd MMM yyyy', { locale: fr });
          const formattedTime = format(date, 'HH:mm');

          return (
            <div>
              <div className="text-sm font-medium">{formattedDate}</div>
              <div className="text-xs text-gray-500">{formattedTime}</div>
            </div>
          );
        }
      },
      {
        key: QuoteTableRowKey.QUOTE_IDENTIFIER,
        label: t('QUOTE.LABELS.QUOTE_NUMBER'),
        sortKey: 'identifier',
        render: (value, row) => {
          const quoteIdentifier = value as IdLabel;
          // Pastille de notifications : Courtier uniquement (matrice centrale)
          const unreadCount = canSeeNotifBadges ? row[QuoteTableRowKey.UNREAD_NOTIFICATION_COUNT] || 0 : 0;
          return (
            <div
              onClick={
                canOpenProcess
                  ? () =>
                      quoteIdentifier.id &&
                      goToContractualizationStep(row.riskId, quoteIdentifier.id, row.borrowerProjectId)
                  : undefined
              }
              className={
                canOpenProcess
                  ? 'cursor-pointer inline-flex items-center gap-2'
                  : 'inline-flex items-center gap-2'
              }
            >
              <span
                className={
                  canOpenProcess
                    ? 'font-bold text-primary hover:underline'
                    : 'font-bold text-gray-700'
                }
              >
                {quoteIdentifier && quoteIdentifier.label}
              </span>
              {unreadCount > 0 && (
                <BadgeTooltip content={`${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`}>
                  <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-secondary text-white text-[10px] font-semibold rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </BadgeTooltip>
              )}
            </div>
          );
        }
      },
      {
        key: QuoteTableRowKey.INSUREDS,
        label: t('COMMON.LABELS.INSURED_PERSONS'),
        sortKey: 'insured'
      },
      {
        // Affiche le libellé lisible (productLabel) avec le code en sous-titre.
        // Le tri suit la colonne affichée : productLabel (nom), pas product (code).
        key: QuoteTableRowKey.PRODUCT,
        label: t('COMMON.LABELS.PRODUCT'),
        sortKey: 'productLabel',
        render: (value, row) => (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {row.productLabel || (value as string)}
            </div>
            {row.productLabel && (
              <div className="text-xs text-gray-500">{value as string}</div>
            )}
          </div>
        )
      },
      {
        // tunnelLabel se trie via la clé back "tunnel" (même valeur)
        key: 'tunnelLabel',
        label: 'Tunnel',
        sortKey: 'tunnel',
        render: (value) => <span className="text-sm text-gray-700">{(value as string) || '—'}</span>
      },
      {
        key: QuoteTableRowKey.SUPPORT,
        label: t('COMMON.LABELS.SUPPORT')
      },
      {
        key: 'effectiveDate',
        label: "Date d'effet",
        sortKey: 'effectiveDate',
        render: (value) => <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>
      },
      {
        key: 'endDate',
        label: 'Validité',
        sortKey: 'endDate',
        render: (value) => <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>
      },
      {
        key: QuoteTableRowKey.OWNER,
        label: t('COMMON.LABELS.INSURER')
      },
      // Colonne Courtier : admin OU vue partagée (brokerName rempli). Tri courtier
      // garanti seulement en admin -> pas de sortKey hors admin.
      ...(admin || isSharedView
        ? ([
            {
              key: 'brokerName',
              label: 'Courtier',
              ...(admin ? { sortKey: 'broker' } : {})
            }
          ] as Column<QuoteRow>[])
        : []),
      // Colonne Plateforme : admin uniquement.
      ...(admin
        ? ([
            {
              key: 'plateformeName',
              label: 'Plateforme',
              sortKey: 'plateforme'
            }
          ] as Column<QuoteRow>[])
        : []),
      {
        key: QuoteTableRowKey.CLIENT_PRICE,
        label: t('PRICING.LABELS.CLIENT_PRICE'),
        sortKey: 'clientPrice'
      },
      {
        key: QuoteTableRowKey.STATUS,
        label: t('COMMON.STATUS'),
        sortable: true,
        sortKey: 'status',
        render: (value) => {
          const status = value as Status;
          return (
            <GenericBadge
              label={status.label}
              color={status.color}
              badgeType="sm-bold"
            />
          );
        }
      }
    ],
    [t, goToContractualizationStep, admin, isSharedView, canOpenProcess, canSeeNotifBadges]
  );

  const moreMenu = useMemo(
    () => ({
      actions: [
        {
          options: {
            icon: 'fileText'
          },
          key: 'viewDocuments',
          label: t('CONTRACT.ACTIONS.VIEW_DOCUMENTS'),
          action: (row: QuoteRow) => {
            openDocumentViewer({
              strategy: DocumentScopeEnum.Quote,
              entityId: row.quoteIdentifier.id
            });
          }
        },
        {
          options: {
            icon: 'arrowRightLeft'
          },
          key: 'transformToContract',
          label: t('QUOTE.ACTIONS.TRANSFORM_TO_CONTRACT'),
          action: (row: QuoteRow) => {
            goToContractualizationStep(row.riskId, row.quoteIdentifier.id,row.borrowerProjectId);
          }
        },
        {
          options: {
            icon: 'send'
          },
          key: 'sendQuote',
          label: t('QUOTE.ACTIONS.SEND_QUOTE'),
          action: (row: QuoteRow) => {
            console.log('Sending quote:', row.quoteIdentifier.id);
          }
        },
        {
          options: {
            icon: 'download'
          },
          key: 'downloadQuote',
          label: t('QUOTE.ACTIONS.DOWNLOAD_QUOTE'),
          action: (row: QuoteRow) => {
            console.log('Downloading quote:', row.quoteIdentifier.id);
          }
        }
      ]
    }),
    [t, goToContractualizationStep, openDocumentViewer]
  );

  const createMenuItems = useCallback(
    (row: QuoteRow) => {
      return moreMenu.actions.map((action) =>
        createMenuItem(
          action.key,
          action.label,
          (data) => {
            action.action(data.row);
          },
          {
            ...action.options,
            data: {
              row: row,
              quoteId: row.quoteIdentifier.id
            }
          }
        )
      );
    },
    [createMenuItem, moreMenu]
  );

  const actions = useMemo(
    () => ({
      ...(hideCreate ? {} : {
        create: {
          label: t('FEATURES.ACTIONS.NEW_QUOTE'),
          onClick: onNewQuote
        }
      }),
      bulk: [
        {
          label: t('COMMON.DELETE'),
          icon: <Trash2 size={16} />,
          variant: 'danger' as const,
          onClick: (rows: QuoteRow[]) => {
            console.log('Delete quotes:', rows.length);
          }
        }
      ],
      row: [
        {
          label: 'seeMore',
          render: (row: QuoteRow, index: number) => (
            <Button
              key={index}
              color="slate"
              round
              onClick={() =>
                // Même cible que le clic sur le numéro de devis : pour un devis emprunteur,
                // on passe le borrowerProjectId pour construire la route /contractualisation/borrower/...
                goToContractualizationStep(
                  row.riskId,
                  row.quoteIdentifier.id,
                  row.borrowerProjectId
                )
              }
            >
              <EyeIcon />
            </Button>
          )
        },
        {
          label: 'moreMenu',
          render: (row: QuoteRow, index: number) => (
            <DropdownButton
              key={index}
              width="w-64"
              anchor="bottom"
              items={createMenuItems(row)}
            />
          )
        }
      ]
    }),
    [t, onNewQuote, navigate, createMenuItems, hideCreate, goToContractualizationStep]
  );

  const fetchQuotes = useCallback(
    async (params: QuoteFetchParams): Promise<TableResponse<QuoteRow>> => {
      try {
        const command: GetGenericListCommandDto = {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          // Tri serveur par colonne (omis -> tri par défaut du back)
          sortBy: (params as any).sortBy,
          sortDirection: (params as any).sortDirection,
          // Filtres du panneau à facettes (indexés par facet.key) -> FiltersDto
          filters: mapFacetFiltersToFiltersDto((params as any).filters)
        };

        const result = admin
          ? await quotesAPI.getAdminQuotes({ getGenericListCommandDto: command })
          : await quotesAPI.getQuotes({ getGenericListCommandDto: command });

        const items = (result.items || []) as QuoteSummaryDto[];
        let mappedQuotes = mapQuoteSummaries(items);

        if (admin) {
          // La liste admin porte en plus les dimensions courtier / plateforme
          mappedQuotes = mappedQuotes.map((row, index) => ({
            ...row,
            brokerName: (items[index] as any)?.brokerName,
            plateformeName: (items[index] as any)?.plateformeName
          }));
        } else {
          // Vue partagée : brokerName rempli par le back sur les routes courtier.
          mappedQuotes = mappedQuotes.map((row, index) => ({
            ...row,
            brokerName: (items[index] as any)?.brokerName
          }));
          detectFromItems(items as Array<{ brokerName?: string | null }>);
        }

        return {
          items: mappedQuotes,
          totalItems: result.totalCount || mappedQuotes.length,
          totalPages: Math.ceil(
            (result.totalCount || mappedQuotes.length) / params.pageSize
          ),
          currentPage: params.pageNumber
        };
      } catch (error) {
        console.error('Erreur lors de la récupération des quotes:', error);
        throw error;
      }
    },
    [admin, detectFromItems]
  );

  const fetchQuoteFacets = useCallback(
    (command: FacetQueryCommand) =>
      admin
        ? quotesAPI.getAdminQuoteListFacets({ getGenericListCommandDto: command })
        : quotesAPI.getQuoteListFacets({ getGenericListCommandDto: command }),
    [admin]
  );

  return (
    <SimpleDashboardContent>
      {/* Barre de recherche globale : espace courtier uniquement, pas en admin BO */}
      {!admin && <DashboardHeader />}
      {/* Identité de page admin (le titre du tableau est porté par ce header) */}
      {admin && <GenericViewHeader title="Devis" icon={FileText} section="Activité" />}
      <ServerTable<QuoteRow>
        apiConfig={{
          fetchData: fetchQuotes
        }}
        columns={columns}
        title={admin ? undefined : {
          iconKey: 'receiptText',
          label: t('FEATURES.TITLE.MY_QUOTES')
        }}
        actions={canRowActions ? actions : undefined}
        selectable
        serverSorting
        centerColumns
        columnCustomization={{
          storageKey: admin ? 'bo-devis' : 'courtier-devis',
          defaultHidden: admin ? undefined : COURTIER_HIDDEN_COLUMNS
        }}
        facets={{ fetchFacets: fetchQuoteFacets, booleanFacets: INCLUDE_TESTS_BOOLEAN_FACET }}
        // Export CSV (admin) : rejoue la commande courante sur la route .../csv
        onExport={
          admin
            ? (p) =>
                exportListCsv(
                  '/api/backoffice/devis/admin/liste/csv',
                  buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
                )
            : undefined
        }
        onRowClick={(row) => console.log('Quote clicked:', row.quoteIdentifier)}
      />
    </SimpleDashboardContent>
  );
};

export default QuoteView;
